import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { useAuth } from './useAuth';
import { useOffline } from './useOffline';

export type UserStateType = 
  | 'authenticated-online'
  | 'authenticated-offline' 
  | 'guest-online'
  | 'guest-offline';

export interface UserStateInfo {
  type: UserStateType;
  user: User | null;
  isOnline: boolean;
  isAuthenticated: boolean;
  canSync: boolean;
  canSignIn: boolean;
  canSignOut: boolean;
  displayName: string;
  statusIcon: string;
  statusColor: string;
  description: string;
}

interface UserStateContextType {
  userState: UserStateInfo;
  loading: boolean;
}

const UserStateContext = createContext<UserStateContextType | undefined>(undefined);

export const UserStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const { isOffline } = useOffline();
  const [userState, setUserState] = useState<UserStateInfo | null>(null);

  useEffect(() => {
    if (authLoading) return;

    const isOnline = !isOffline;
    const isAuthenticated = !!user;

    let type: UserStateType;
    if (isAuthenticated && isOnline) {
      type = 'authenticated-online';
    } else if (isAuthenticated && !isOnline) {
      type = 'authenticated-offline';
    } else if (!isAuthenticated && isOnline) {
      type = 'guest-online';
    } else {
      type = 'guest-offline';
    }

    const stateInfo: UserStateInfo = {
      type,
      user,
      isOnline,
      isAuthenticated,
      canSync: isAuthenticated && isOnline,
      canSignIn: !isAuthenticated && isOnline,
      canSignOut: isAuthenticated,
      ...getStateDisplayInfo(type)
    };

    setUserState(stateInfo);
  }, [user, isOffline, authLoading]);

  const value: UserStateContextType = {
    userState: userState || getDefaultState(),
    loading: authLoading || !userState
  };

  return (
    <UserStateContext.Provider value={value}>
      {children}
    </UserStateContext.Provider>
  );
};

function getStateDisplayInfo(type: UserStateType) {
  switch (type) {
    case 'authenticated-online':
      return {
        displayName: 'Authenticated Account',
        statusIcon: '✅',
        statusColor: 'text-green-600',
        description: 'Your progress is automatically saved and synced'
      };
    case 'authenticated-offline':
      return {
        displayName: 'Authenticated (Offline)',
        statusIcon: '📶',
        statusColor: 'text-orange-600',
        description: 'Your progress is saved locally and will sync when online'
      };
    case 'guest-online':
      return {
        displayName: 'Guest Account',
        statusIcon: '👤',
        statusColor: 'text-gray-500',
        description: 'Sign in to sync your progress across devices'
      };
    case 'guest-offline':
      return {
        displayName: 'Offline Mode',
        statusIcon: '📱',
        statusColor: 'text-blue-600',
        description: 'Your progress is saved locally while offline'
      };
  }
}

function getDefaultState(): UserStateInfo {
  return {
    type: 'guest-offline',
    user: null,
    isOnline: false,
    isAuthenticated: false,
    canSync: false,
    canSignIn: false,
    canSignOut: false,
    ...getStateDisplayInfo('guest-offline')
  };
}

export const useUserState = (): UserStateContextType => {
  const context = useContext(UserStateContext);
  if (context === undefined) {
    throw new Error('useUserState must be used within a UserStateProvider');
  }
  return context;
}; 